import { useState, useRef, useEffect } from "react";
import { get, put, post, get_image_url, postMultipart } from "../api";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { IconTrash, IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import cn from "../utils/cn";
async function getBanners() {
  return await get("/site_images/banners");
}
async function updateBanners(images) {
  return await put("/site_images/banners", images);
}
function useBanners() {
  const queryClient = useQueryClient();
  const [state, setState] = useState([]);
  const _imageQuery = useQuery({
    queryKey: ["banners"],
    queryFn: getBanners,
    onSuccess: (data) => setState(data),
    staleTime: Infinity,
  });
  const mutation = useMutation({
    mutationFn: updateBanners,
    onSuccess: () => queryClient.invalidateQueries(["banners"]),
  });
  const save = (images) => mutation.mutate(images);
  return { state, save };
}

function Banners() {
  const banners = useBanners();
  const [images, setImages] = useState([]);
  const [updated, setUpdated] = useState(false);
  useEffect(() => {
    if (banners.state.length === 0) return;
    setImages(banners.state);
    setUpdated(false);
  }, [banners.state]);
  const fileImportRef = useRef(null);
  const handleClick = (e) => {
    fileImportRef.current?.click();
  };
  const handleSubmit = async (e) => {
    const formData = new FormData();
    Array.from(e.target.files).forEach((x) => {
      formData.append(Array.from(e.target.files).indexOf(x), x);
    });

    const imageResponse = await postMultipart(`/images`, formData);
    imageResponse.forEach((image_id) => addImage(image_id));
  };
  const addImage = (image_id) => {
    setImages([...images, image_id]);
    setUpdated(true);
  };
  const deleteImage = (index) => {
    const newState = [...images];
    newState.splice(index, 1);
    setImages(newState);
    setUpdated(true);
  };
  const swap = (index1, index2) => {
    const newState = [...images];
    [newState[index1], newState[index2]] = [newState[index2], newState[index1]];
    setImages(newState);
    setUpdated(true);
  };
  const discard = () => {
    setImages([...banners.state]);
    setUpdated(false);
  };
  return (
    <div className="border rounded-md m-2 p-4">
      <div className="flex items-center justify-between pb-4">
        <div className="text-lg font-semibold">Banners</div>
        <div className="flex gap-2">
          <button
            className="rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            onClick={handleClick}
          >
            Upload
          </button>
          <button className="rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
            Add
          </button>
          {updated && (
            <>
              <button
                className="rounded-md bg-green-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
                onClick={() => banners.save(images)}
              >
                Save
              </button>
              <button
                className="rounded-md bg-red-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
                onClick={discard}
              >
                Discard
              </button>
            </>
          )}
          <input
            ref={fileImportRef}
            name="image_id"
            accept="image/*"
            type="file"
            multiple
            onChange={handleSubmit}
            className="hidden"
          />
        </div>
      </div>
      <div className="flex flex-col gap-2 max-w-full">
        {images.map((image_id, idx) => (
          <div
            className="flex w-100 overflow-hidden"
            key={`${image_id}-${idx}`}
          >
            <div className="my-4 me-2">
              <button
                onClick={() => deleteImage(idx)}
                className="text-red-500 hover:text-red-300 mb-8"
              >
                <IconTrash />
              </button>
              <button
                onClick={() => {
                  if (idx === 0) return;
                  swap(idx, idx - 1);
                }}
                className={cn(idx === 0 ? "text-gray-300" : "text-black")}
              >
                <IconChevronUp />
              </button>
              <button
                onClick={() => {
                  if (idx === images.length - 1) return;
                  swap(idx, idx + 1);
                }}
                className={cn(
                  idx === images.length - 1 ? "text-gray-300" : "text-black"
                )}
              >
                <IconChevronDown />
              </button>
            </div>
            <img src={get_image_url(image_id)} alt="..." className="" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default Banners;
